import { PageLayout } from '@/layouts/page-layout'
import { GroupInput } from '@/components/v2/group-input'
import { Button, Typography, Spinner } from '@material-tailwind/react'
import {
  useLoaderData,
  useParams,
  useNavigate,
  useLocation 
} from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Form, Formik } from 'formik'
import { useEffect, useMemo, useState, useContext } from 'react'
import { CustomYup, toOptions } from '@/utils'
import { ContractSearcherContext } from '@/components/contract-searcher/context'
import { ConfirmationModal } from '@/components/confirmation-modal'
import { NotificationModal } from '@/components/notification-modal';
import { StatusSwitcher } from '@/components/status-switcher'
import { CountdownTimer } from '@/components/countdown-timer'
import { HasAccess } from '@permify/react-role'
import {
  ContractInformationForm,
  EmployeeInformationForm
} from '../shared/forms'
import { list } from '@/router/utils'
import {
  useUpdateEmployeeMutation,
  useDeleteEmployeeMutation
} from '@/store/services'

const validationSchema = CustomYup.object({
  employee_contracts_attributes: CustomYup.object({
    contract_type: CustomYup.string().required('Tipo de contrato es requerido'),
    employee_role_id: CustomYup.string().required('Rol es requerido'),
    start_date: CustomYup.date().required('Fecha de inicio es requerida'),
    register_attendance: CustomYup.boolean(),
    working_hours: CustomYup.number().nullable().when('register_attendance', {
      is: true,
      then: () => CustomYup.number().required('Horas semanales es requerido')
    }),
    finish_date: CustomYup.date()
    .nullable()
    .transform((value, originalValue) => {
      return originalValue === '' ? null : value;
    })
    .when('contract_type', (contract_type, schema) => {
      if (contract_type == 'fixed_term') {
        return schema
          .required('La fecha de vencimiento es obligatoria')
          .min(new Date(), 'La fecha debe ser futura')
          .typeError('Formato de fecha inválido');
      }
      return schema.nullable().notRequired();
    }),
    contractor_contract_id: CustomYup.number()
      .required('Selecciona un contratista y un contrato')
      .typeError('Debes seleccionar un contratista válido'),
  }),
  has_local_document_number: CustomYup.boolean(),
  document_number: CustomYup.string()
    .rut()
    .when('has_local_document_number', {
      is: false,
      then: () => CustomYup.string()
    })
})

export const EmployeeEdit = ({ mode = 'edit' }) => {
  // Estados
  const [isFinalizing, setIsFinalizing] = useState(false)
  const [submitError, setSubmitError] = useState(null)
  const [tempEmployeeData, setTempEmployeeData] = useState(null)
  const [loadingTempData, setLoadingTempData] = useState(mode === 'create')
  const [employeeHasContract, setEmployeeHasContract] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdEmployee, setCreatedEmployee] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [expiresAt, setExpiresAt] = useState(null);

  // Hooks y contexto
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { id: employeeId } = useParams()
  const { updateCurrentContract } = useContext(ContractSearcherContext)

  // Datos
  const loaderData = useLoaderData() || {};
  const { countriesData, employeeData, error, loading } = loaderData;
  const countries = Array.isArray(countriesData?.countries) 
  ? countriesData.countries 
  : countriesData?.data?.countries || [];

  const [currentEmployee, setCurrentEmployee] = useState(
    employeeData?.employee || employeeData
  );

  const [updateEmployee] = useUpdateEmployeeMutation()
  const [deleteEmployee] = useDeleteEmployeeMutation()

  // Modal states
  const [openStatusSwitcherModal, setOpenStatusSwitcherModal] = useState(false)
  const [openAccreditedSwitcherModal, setOpenAccreditedSwitcherModal] = useState(false)
  

  // Valores iniciales
  const initialValues = mode === 'create'
  ? {
      name: '',
      last_name: '',
      document_number: '',
      birth_date: '',
      country_id: null,
      gender: 'male',
      disability: false,
      pensioner: false,
      has_local_document_number: true,
      employee_contracts_attributes: {},
      email: '',

      // Personal data flatten
      phone: tempEmployeeData?.personal_data?.phone || '',
      address: tempEmployeeData?.personal_data?.address || '',
      region_id: tempEmployeeData?.personal_data?.region_id || '',
      marital_status: tempEmployeeData?.personal_data?.marital_status || '',

      // Address form compatibility
      address_attributes: {
        street: tempEmployeeData?.personal_data?.address || '',
        state_id: tempEmployeeData?.personal_data?.region_id || ''
      },

      // Temp main data fallback
      ...tempEmployeeData,
      country_id: tempEmployeeData?.country_id || null
    }
  : {
    id: currentEmployee?.id,
    name: currentEmployee?.name,
    last_name: currentEmployee?.lastName,
    document_number: currentEmployee?.documentNumber,
    birth_date: currentEmployee?.birthDate,
    country_id: currentEmployee?.country?.id || currentEmployee?.country_id || null,
    gender: currentEmployee?.gender,
    email: currentEmployee?.email,
    disability: currentEmployee?.disability,
    pensioner: currentEmployee?.pensioner,
    attachments_attributes: [],
    status: currentEmployee?.status,
    accredited: currentEmployee?.accredited,
    has_local_document_number: currentEmployee?.hasLocalDocumentNumber,

    phone: currentEmployee?.personalData?.phone || '',
    address: currentEmployee?.personalData?.address || '',
    region_id: currentEmployee?.personalData?.region_id || '',
    marital_status: currentEmployee?.personalData?.marital_status || '',

    address_attributes: {
      street: currentEmployee?.personalData?.address || '',
      state_id: currentEmployee?.personalData?.region_id || '',
    },
    employee_contracts_attributes: {
      id: currentEmployee?.activeContract?.id || null,
      contractor_contract_id: currentEmployee?.activeContract?.contract?.id,
      contract_type: currentEmployee?.activeContract?.contractType,
      start_date: currentEmployee?.activeContract?.startDate,
      finish_date: currentEmployee?.activeContract?.finishDate,
      assignation_date: currentEmployee?.activeContract?.assignationDate,
      employee_role_id: currentEmployee?.activeContract?.employeeRole?.id,
      register_attendance: currentEmployee?.activeContract?.registerAttendance,
      working_hours: currentEmployee?.activeContract?.workingHours
    }
  }

  const countryOptions = useMemo(() => 
    toOptions({
      data: countries,
      value: 'id',
      label: 'name'
    }), 
    [countries]
  )

  // Efectos
  useEffect(() => {
    if (mode === 'create' && location.search.includes('temp_hash')) {
      const loadTempData = async () => {
        try {
          const tempHash = new URLSearchParams(location.search).get('temp_hash');
          const response = await fetch(`/api/v1/employees/temp_data?temp_hash=${tempHash}`);
          
          if (response.status === 404) {
            navigate('/employees/create');
            return;
          }
          
          if (!response.ok) throw new Error("Error cargando datos temporales");
          
          const tempData = await response.json();
          
          setTempEmployeeData({
            ...tempData,
            country_id: tempData.country_id
          });
          
          if (tempData.expires_at) {
            setExpiresAt(tempData.expires_at);
          }
          
        } catch (error) {
          console.error('Error:', error);
          // Solo mostrar error si no es 404
          if (error.message !== "Error cargando datos temporales") {
            setSubmitError(t('errors.load_temp_data'));
          }
        } finally {
          setLoadingTempData(false);
        }
      };
      loadTempData();
    }
  }, [location.search, mode, navigate]); 

  useEffect(() => {
    if (currentEmployee) {
      const hasContract = !!currentEmployee?.activeContract?.contract?.id
      setEmployeeHasContract(hasContract)
    }
  }, [currentEmployee])

  useEffect(() => {
    if (mode === 'edit' && currentEmployee) {
      updateCurrentContract(currentEmployee.activeContract?.contract);
    }
  }, [currentEmployee, mode, updateCurrentContract]);

  useEffect(() => {
    if (loaderData?.employeeData) {
      setCurrentEmployee(loaderData.employeeData.employee || loaderData.employeeData);
    }
  }, [loaderData]);
  

  // Handlers
  const handleFinalizeCreation = async (formData) => {
    setIsFinalizing(true);
    setSubmitError(null);

    try {
      const payload = {
        ...formData,
        employee_contracts_attributes: {
          "0": formData.employee_contracts_attributes
        }
      }

      // Eliminar campos no deseados
      delete payload.address_attributes
      const tempHash = new URLSearchParams(location.search).get('temp_hash');
      const response = await fetch('/api/v1/employees/finalize_create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          temp_hash: tempHash,
          employee: payload
        })
      })
      if (!response.ok) throw new Error('Error al finalizar la creación');
      
      const { employee } = await response.json();
      setCreatedEmployee(employee);
      setShowSuccessModal(true);

    } catch (error) {
      setSubmitError(error.message || t('errors.creation_failed'));
    } finally {
      setIsFinalizing(false);
    }
  };

  const handleSwitchAccredited = async () => {
    try {
      const newAccreditedState = currentEmployee?.accredited === 'active' 
        ? 'inactive' 
        : 'active';
      
      await updateEmployee({ 
        id: currentEmployee.id, 
        accredited: newAccreditedState 
      }).unwrap();
  
      setCurrentEmployee(prev => ({
        ...prev,
        accredited: newAccreditedState
      }));
      
    } catch (error) {
      console.error('Error actualizando acreditación:', error);
    }
  }
  
  const handleSwitchStatus = async () => {
    try {
      const newStatus = currentEmployee?.status === 'active' 
        ? 'inactive' 
        : 'active';
      
      await updateEmployee({ 
        id: currentEmployee.id, 
        status: newStatus 
      }).unwrap();
  
      setCurrentEmployee(prev => ({
        ...prev,
        status: newStatus
      }));
      
    } catch (error) {
      console.error('Error actualizando estado:', error);
    }
  }

  const handleCancel = () => {
    if (mode === 'create') {
      setShowCancelModal(true);
    } else {
      navigate(list('employees'));
    }
  };

  const handleConfirmCancel = async () => {
    try {
      const tempHash = new URLSearchParams(location.search).get('temp_hash');
      if (tempHash) {
        await fetch(`/api/v1/employees/clear_temp?temp_hash=${tempHash}`, { 
          method: 'DELETE' 
        });
      }
    } catch (error) {
      console.error('Error limpiando borrador:', error);
    } finally {
      navigate(list('employees'));
    }
  };

  const handleExpiration = () => {
    if (mode === 'create') {
      handleConfirmCancel(); // Usar el mismo handler de cancelación sin modal
    }
  };

  const onSubmit = async (values) => {
    // Asegurar que los campos vengan desde address_attributes si fueron modificados
    const finalAddress = values?.address_attributes?.street || values.address || ''
    const finalRegionId = values?.address_attributes?.state_id || values.region_id || ''

    const payload = {
      ...values,
      address: finalAddress,
      region_id: finalRegionId,
      personal_data: {
        phone: values.phone || null,
        address: finalAddress || null,
        region_id: finalRegionId || null,
        marital_status: values.marital_status || null
      }
    }

    if (mode === 'create') {
      await handleFinalizeCreation(payload)
    } else {
      try {
        await updateEmployee({ id: employeeId, ...payload }).unwrap()
        navigate(list('employees'))
      } catch (error) {
        setSubmitError(t('errors.update_failed'))
      }
    }
  };

  // Efecto de limpieza
  useEffect(() => {
    return () => {
      if (location.state?.fromCreate && !employeeHasContract && currentEmployee?.id) {
        deleteEmployee(currentEmployee.id).catch(console.error)
      }
    }
  }, [location.state, employeeHasContract, currentEmployee, deleteEmployee])

  // Estados de carga y error
  if (loadingTempData || loading) return <div>Cargando datos...</div>;
  if (error) return <div>Error cargando el empleado: {error}</div>;
  if (!currentEmployee && mode !== 'create') return <div>Empleado no encontrado</div>;

  return (
    <PageLayout
      editMode
      title={t(mode == 'create' ? 'actions.create.with' : 'actions.edit.with', { what: t('employee.one') })}
      rightSection={
        mode === 'create' ? (
          expiresAt && (
            <CountdownTimer 
              expiresAt={expiresAt}
              onExpire={handleExpiration}
              className="text-red-600 font-medium"
            />
          )
        ) : (
          mode === 'edit' && (
            <>
              <HasAccess permissions='employee#activate'>
                <StatusSwitcher
                  label={'employee'}
                  type={'status'}
                  value={currentEmployee?.status}
                  onChange={() => setOpenStatusSwitcherModal(true)}
                />
              </HasAccess>
              <HasAccess permissions='employee#accredit'>
                <StatusSwitcher
                  label={'employee'}
                  type={'accredited'}
                  value={currentEmployee?.accredited}
                  onChange={() => setOpenAccreditedSwitcherModal(true)}
                />
              </HasAccess>
            </>
          )
        )
      }
    >
      {showSuccessModal && (
        <NotificationModal
          iconType="check"
          title={t('employees.success.title')}
          message={t('notifications.success.create.one', { 
            what: t('employee.one')
          })}
          returnPath={list('employees')}
          setBackStateFunction={() => setShowSuccessModal(false)}
        />
      )}
      <ConfirmationModal
        onConfirm={handleSwitchStatus}
        open={openStatusSwitcherModal}
        handler={() => setOpenStatusSwitcherModal(false)}
        title={t(`modal.confirm.status.${currentEmployee?.status}.title`)}
        message={t(`modal.confirm.status.${currentEmployee?.status}.message`)}
      />

      <ConfirmationModal
        onConfirm={handleSwitchAccredited}
        open={openAccreditedSwitcherModal}
        handler={() => setOpenAccreditedSwitcherModal(false)}
        title={t(`modal.confirm.accredited.${currentEmployee?.accredited}.title`)}
        message={t(`modal.confirm.accredited.${currentEmployee?.accredited}.message`)}
      />
      {submitError && (
        <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg">
          {submitError}
        </div>
      )}
        <ConfirmationModal
          open={showCancelModal}
          handler={() => setShowCancelModal(false)}
          onConfirm={handleConfirmCancel}
          title={t('employees.cancel_modal.title')}
          message={t('employees.cancel_modal.message')}
          confirmButtonText={t('employees.cancel_modal.confirm_button')}
        >
          <Typography variant="small" className="mt-2 text-gray-600">
            {t('employees.cancel_modal.additional_info')}
          </Typography>
        </ConfirmationModal>
        <Formik
          validationSchema={validationSchema}
          initialValues={initialValues}
          onSubmit={onSubmit}
          validateOnBlur={false}
          validateOnChange={false}
          validateOnMount={false}
        >
        {({ isSubmitting }) => (
          <Form>
            <GroupInput
              title={t('employee.one')}
              description={t('new.employee.section.personalInformation')}
              className='py-8'
            >
              <EmployeeInformationForm countryOptions={countryOptions} />
            </GroupInput>

            <GroupInput
              title={t('contract.one')}
              description={t('new.employee.section.contractInformation')}
              className='py-14'
              divider={false}
            >
              <ContractInformationForm employee={currentEmployee} />
            </GroupInput>

            <div className='flex justify-end gap-3 mt-10'>
              <Button
                variant='text'
                type='button'
                onClick={handleCancel}
              >
                {t('actions.cancel')}
              </Button>
              <HasAccess permissions='employee#update'>
              <Button 
                type="submit" 
                disabled={isSubmitting || isFinalizing}
              >
                {mode === 'create' ? 'Finalizar creación' : 'Actualizar empleado'}
                {(isSubmitting || isFinalizing) && <Spinner />}
              </Button>
              </HasAccess>
            </div>
          </Form>
        )}
      </Formik>
    </PageLayout>
  )
}